import { FormInput } from "@/components/forms/FormInput";
import { FormSelect } from "@/components/forms/FormSelect";
import { FormSubmitButton } from "@/components/forms/FormSubmitButton";
import { FormWrapper } from "@/components/forms/FormWrapper";
import { validation } from "@/domain/validation";
import { User, user } from "@/endpoints/dryp/user";
import { useAsyncStateStore } from "@/hooks/useAsyncStateStore";
import { useFormHandler } from "@/hooks/useFormHandler";
import { observer } from "mobx-react-lite";
import { z } from "zod";

const ROLE_OPTIONS = [
	{ label: "Admin", value: "Admin" },
	{ label: "User", value: "User" },
	{ label: "Guest", value: "Guest" },
];

const noWhitespace = z.string().min(1, "Username is required").regex(/^\S+$/, "Username must not contain spaces");

const createSchema = z
	.object({
		userName: noWhitespace,
		email: validation.email,
		password: validation.passwordWithRules,
		confirmPassword: validation.passwordWithRules,
		roleName: z.string().min(1, "Role is required"),
	})
	.refine(({ password, confirmPassword }) => password === confirmPassword, {
		message: "Passwords do not match",
		path: ["confirmPassword"],
	});

const editSchema = z.object({
	userName: noWhitespace,
	email: validation.email,
	roleName: z.string().min(1, "Role is required"),
});

interface FormProps {
	defaultUserValues?: User;
	title?: string;
	onSuccess?: () => void;
}

export const Form: React.FC<FormProps> = observer(({ title = "Create", defaultUserValues, onSuccess }) => {
	const isEditing = !!defaultUserValues;
	const schema = isEditing ? editSchema : createSchema;
	const createApi = useAsyncStateStore(user.createUser);
	const updateApi = useAsyncStateStore(user.updateUser);

	const { form, handleFormErrors } = useFormHandler(schema, {
		defaultValues: {
			userName: defaultUserValues?.userName ?? "",
			email: defaultUserValues?.email ?? "",
			roleName: defaultUserValues?.roleName ?? "",
			...(isEditing ? {} : { password: "", confirmPassword: "" }),
		},
	});

	const onSubmit = async (data: z.infer<typeof createSchema> | z.infer<typeof editSchema>) => {
		try {
			if (isEditing) {
				const { userName, email, roleName } = data;
				await updateApi.fetch(defaultUserValues.id, { userName, email, roleName });
			} else {
				const { userName, email, roleName, password } = data as z.infer<typeof createSchema>;
				await createApi.fetch({ userName, email, roleName, password });
			}
			onSuccess?.();
		} catch (error) {
			handleFormErrors(error);
		}
	};

	const loading = createApi.loading || updateApi.loading;
	const error = createApi.error || updateApi.error;

	return (
		<FormWrapper form={form} onSubmit={onSubmit} error={error}>
			<FormInput name="userName" type="text" label="Username" placeholder="Type username" required />
			<FormInput name="email" type="email" label="Email" placeholder="Type email" required />
			<FormSelect name="roleName" label="Role" options={ROLE_OPTIONS} required />
			{!isEditing && (
				<>
					<FormInput name="password" type="password" label="Password" placeholder="Type password" required />
					<FormInput
						name="confirmPassword"
						type="password"
						label="Confirm password"
						placeholder="Repeat password"
						required
					/>
				</>
			)}
			<FormSubmitButton isLoading={loading} buttonText={`${title} user`} buttonLoadingText={`${title}ing user`} />
		</FormWrapper>
	);
});
